import { renderBreadcrumb } from "./components/breadcrumb.js";

// Each route's `loader` is a dynamic import — webpack code-splits it
// into its own chunk, fetched only the first time that route is
// visited. `trail` is the static breadcrumb ancestry for the route;
// a route whose page module returns a `breadcrumbLabel` (e.g. the
// variety detail page) has that value swap in for the "__CURRENT__"
// placeholder.
const routes = [
  {
    pattern: /^\/$/,
    loader: () => import(/* webpackChunkName: "page-home" */ "./pages/home.js"),
    trail: null,
  },
  {
    pattern: /^\/products$/,
    loader: () => import(/* webpackChunkName: "page-products" */ "./pages/products.js"),
    trail: [{ label: "__CURRENT__" }],
  },
  {
    pattern: /^\/products\/([^/]+)$/,
    loader: () => import(/* webpackChunkName: "page-product-detail" */ "./pages/product-detail.js"),
    trail: [{ label: "Products", href: "/products" }, { label: "__CURRENT__" }],
    params: ["slug"],
  },
  {
    pattern: /^\/varieties$/,
    loader: () => import(/* webpackChunkName: "page-varieties" */ "./pages/varieties.js"),
    trail: [{ label: "__CURRENT__" }],
  },
  {
    pattern: /^\/varieties\/([^/]+)$/,
    loader: () => import(/* webpackChunkName: "page-variety-detail" */ "./pages/variety-detail.js"),
    trail: [{ label: "Varieties", href: "/varieties" }, { label: "__CURRENT__" }],
    params: ["slug"],
  },
  {
    pattern: /^\/process$/,
    loader: () => import(/* webpackChunkName: "page-process" */ "./pages/process.js"),
    trail: [{ label: "__CURRENT__" }],
  },
  {
    pattern: /^\/certifications$/,
    loader: () => import(/* webpackChunkName: "page-certifications" */ "./pages/certifications.js"),
    trail: [{ label: "__CURRENT__" }],
  },
  {
    pattern: /^\/faqs$/,
    loader: () => import(/* webpackChunkName: "page-faqs" */ "./pages/faqs.js"),
    trail: [{ label: "__CURRENT__" }],
  },
  {
    pattern: /^\/contact$/,
    loader: () => import(/* webpackChunkName: "page-contact" */ "./pages/contact.js"),
    trail: [{ label: "__CURRENT__" }],
  },
];

const NOT_FOUND_REDIRECT = "/varieties";

// Keep in sync with SITE_URL in prerender.js.
const SITE_URL = "https://www.vivardhaa.com";

const DEFAULT_DESCRIPTION =
  "Vivardhaa — Guntur Mirchi procured, destemmed, sun-cured and graded with technology coordinating ten operating teams in real time.";

// Normalizes away a trailing slash (except the root) so "/products/"
// and "/products" resolve to the same route — some servers add the
// trailing slash automatically when serving a directory's index.html.
const parsePath = () => {
  const raw = location.pathname || "/";
  return raw.length > 1 && raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

const matchRoute = (path) => {
  for (const route of routes) {
    const match = path.match(route.pattern);
    if (match) {
      const params = {};
      (route.params || []).forEach((name, i) => (params[name] = decodeURIComponent(match[i + 1])));
      return { route, params };
    }
  }
  return null;
};

const setActiveNav = (path) => {
  const topLevel = `/${path.split("/")[1] || ""}`;
  document.querySelectorAll(".nav__list a").forEach((a) => {
    const href = a.getAttribute("href") || "";
    const isActive = href === topLevel;
    a.classList.toggle("is-active", isActive);
  });
};

const setMetaDescription = (description) => {
  document
    .querySelector('meta[name="description"]')
    ?.setAttribute("content", description || DEFAULT_DESCRIPTION);
};

// Canonical link and Open Graph tags default to the homepage in
// src/index.html — without this, every route would report the
// homepage's URL/title/description to link previews and AI crawlers
// that read OG tags instead of re-deriving them from the DOM.
const setSocialMeta = (path, title, description) => {
  const url = `${SITE_URL}${path}`;
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);
  document.querySelector('meta[property="og:url"]')?.setAttribute("content", url);
  document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
  document
    .querySelector('meta[property="og:description"]')
    ?.setAttribute("content", description || DEFAULT_DESCRIPTION);
};

let currentCleanup = null;
let currentRender = null;

// Programmatic navigation for JS-driven links (e.g. a "More details"
// button) — pushes real browser history so back/forward and reload
// keep working like plain anchors would.
export function navigate(path) {
  if (path !== location.pathname) {
    history.pushState(null, "", path);
  }
  if (currentRender) currentRender();
}

export function initRouter(outlet) {
  const render = async () => {
    const path = parsePath();
    const matched = matchRoute(path) || matchRoute(NOT_FOUND_REDIRECT);

    if (typeof currentCleanup === "function") {
      currentCleanup();
      currentCleanup = null;
    }

    const { route, params } = matched;
    const mod = await route.loader();
    const page = mod.default(params);

    let breadcrumbHtml = "";
    if (route.trail) {
      const trail = route.trail.map((item) =>
        item.label === "__CURRENT__" ? { ...item, label: page.breadcrumbLabel || page.title } : item
      );
      breadcrumbHtml = renderBreadcrumb(trail);
    }

    const fullTitle = page.title ? `${page.title} · Vivardhaa` : "Vivardhaa";
    document.title = fullTitle;
    setMetaDescription(page.description);
    setSocialMeta(path, fullTitle, page.description);
    outlet.innerHTML = breadcrumbHtml + page.html;

    if (typeof page.mount === "function") {
      currentCleanup = page.mount(outlet) || null;
    }

    setActiveNav(path);
    // "instant" overrides the global smooth-scroll CSS so navigating
    // between pages jumps straight to the top instead of animating.
    window.scrollTo({ top: 0, behavior: "instant" });

    // Close the mobile nav drawer on navigation.
    document.querySelector(".nav")?.classList.remove("is-open");
    document.querySelector(".nav-toggle")?.setAttribute("aria-expanded", "false");

    // Signal for the build-time prerender script (see prerender.js),
    // which waits for this before snapshotting the page's HTML.
    window.__ROUTE_READY__ = path;
  };

  currentRender = render;

  window.addEventListener("popstate", render);

  // Intercept clicks on same-origin, path-based links so in-app
  // navigation stays client-side (no full page reload). Plain
  // fragment links (e.g. the "#main" skip-link) and downloads are
  // left alone for native browser handling.
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const anchor = e.target.closest("a");
    if (!anchor) return;
    if (anchor.target && anchor.target !== "_self") return;
    if (anchor.hasAttribute("download")) return;

    const href = anchor.getAttribute("href") || "";
    if (!href.startsWith("/")) return;
    if (anchor.origin !== location.origin) return;

    e.preventDefault();
    navigate(anchor.pathname + anchor.search);
  });

  render();
}
